import LogRocket from 'logrocket';
import Tracker from '@openreplay/tracker';

// Environment variables
export const ANALYTICS_VENDOR = process.env.REACT_APP_ANALYTICS_VENDOR || 'logrocket';
export const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID;
export const OPENREPLAY_PROJECT_KEY = process.env.REACT_APP_OPENREPLAY_KEY;

// Configuration objects
export const LOGROCKET_CONFIG = {
  // Add any additional configuration options here
  // For example:
  // network: {
  //   requestSanitizer: (request) => {
  //     // Sanitize sensitive data from requests
  //     return request;
  //   },
  //   responseSanitizer: (response) => {
  //     // Sanitize sensitive data from responses
  //     return response;
  //   }
  // }
};

export const OPENREPLAY_CONFIG = {
  // Disable SSL requirement for localhost development
  __DISABLE_SECURE_MODE: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
  onError: (error) => {
    console.warn('OpenReplay error (non-critical):', error);
  }
};

// Analytics vendor abstraction
class AnalyticsVendor {
  constructor() {
    this.vendor = null;
    this.isInitialized = false;
    this.vendorType = ANALYTICS_VENDOR;
  }

  async initialize(userData = null) {
    if (navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes') {
      console.log('DoNotTrack is enabled, skipping analytics initialization');
      return false;
    }

    if (this.vendorType === 'openreplay') {
      if (!this.isOpenReplaySupported()) {
        console.warn('OpenReplay not supported in this browser');
      }
    }

    try {
      let initialized = false;
      if (this.vendorType === 'logrocket') {
        initialized = await this.initializeLogRocket();
      } else if (this.vendorType === 'openreplay') {
        initialized = await this.initializeOpenReplay(userData);
      } else {
        console.log(`Unknown analytics vendor: ${this.vendorType}`);
        return false;
      }
      
      this.isInitialized = initialized;
      if (initialized) {
        console.log(`${this.vendorType} initialized successfully`);
      } else {
        console.log(`${this.vendorType} not initialized`);
      }
      return true;
    } catch (error) {
      console.error(`Error initializing ${this.vendorType}:`, error);
      this.isInitialized = false;
      return false;
    }
  }

  isOpenReplaySupported() {
    return (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function' &&
      typeof window.addEventListener === 'function' &&
      typeof window.localStorage !== 'undefined'
    );
  }

  async initializeLogRocket() {
    if (!LOGROCKET_APP_ID || LOGROCKET_APP_ID === 'null' || LOGROCKET_APP_ID === 'undefined') {
      console.log('LogRocket APP_ID not provided');
      return false;
    }
    
    LogRocket.init(LOGROCKET_APP_ID, LOGROCKET_CONFIG);
    this.vendor = LogRocket;
    return true;
  }

  async initializeOpenReplay(userData = null) {
    if (!OPENREPLAY_PROJECT_KEY || OPENREPLAY_PROJECT_KEY === 'null' || OPENREPLAY_PROJECT_KEY === 'undefined') {
      console.log('OpenReplay PROJECT_KEY not provided');
      return false;
    }
    
    try {
      this.vendor = new Tracker({
        projectKey: OPENREPLAY_PROJECT_KEY,
        ...OPENREPLAY_CONFIG
      });
      
      try {
        if (userData && userData.email) {
          const metadata = {};
          if (userData.name) {
            metadata.name = userData.name;
          }
          if (userData.email) {
            metadata.email = userData.email;
          }  
          this.vendor.start({
            userID: userData.email,
            metadata: metadata
          });
        } else {
          this.vendor.start();
        }
        return true;
      } catch (startError) {
        if (startError.message && startError.message.includes('Browser doesn\'t support required api')) {
          console.warn('OpenReplay browser compatibility issue detected, falling back to LogRocket');
          return false;
        }
        throw startError;
      }
    } catch (error) {
      console.warn('OpenReplay initialization failed', error.message);
      return false;
    }
  }

  identifyUser(userId, userData = {}) {
    if (!this.isInitialized || !this.vendor) {
      console.warn(`${this.vendorType} not initialized, skipping user identification`);
      return false;
    }

    try {
      if (this.vendorType === 'logrocket') {
        this.vendor.identify(userId, userData);
      } else if (this.vendorType === 'openreplay') {
        this.vendor.setUserID(userData.email);
        if (userData.name) {
          this.vendor.setMetadata('name', userData.name);
        }
        if (userData.email) {
          this.vendor.setMetadata('email', userData.email);
        }
      }
      console.log(`${this.vendorType} user identified successfully`);
      return true;
    } catch (error) {
      console.error(`Error identifying user in ${this.vendorType}:`, error);
      return false;
    }
  }

  getVendor() {
    return this.vendor;
  }

  isReady() {
    return this.isInitialized && this.vendor !== null;
  }

  getVendorType() {
    return this.vendorType;
  }
}

const analytics = new AnalyticsVendor();
window.analytics = analytics;

export default analytics;
